/**
 * /account の「ログイン方法」の中の「ログイン用パスキー」（2026-09-28 creator/passkey-login）。一覧・追加（名前）・削除。
 * 置き場: pages/AccountLoginMethods.tsx の LoginMethodsCard（一覧の読み込みはカードが持つ = 「最後のログイン手段か」と
 *   再認証の方法（loginPasskey）の判断にカードも同じ一覧を使うため）。
 *
 * API の正: apps/server/src/modules/platform/loginPasskeyRoutes.ts（呼び出しは ../loginPasskeyClient.ts）
 *   GET    /v1/developers/me/login-passkeys                → { passkeys }
 *   POST   …/login-passkeys/options                        → { publicKey }（create 用。未確認 403 email_unverified・上限で 409 too_many_passkeys（details.limit））
 *   POST   …/login-passkeys { credential, label?, password? | reauthToken? } → 201
 *   DELETE …/login-passkeys/:id { password? | reauthToken? } → { passkeys }（最後の手段 409 last_login_method・ほかのセッションと kdt_ は失効）
 *
 * 出金用のパスキー（Money の /money/passkeys）とは別の登録・別の表で、互いに使えない。見出しと説明で違いを書き、出金用へのリンクを置く（指示書 2）。
 *
 * 追加の順番（add の WHY）:
 *   1. 本人の確認（部品 components/Reauth.tsx）を **create の前** に済ませる。WHY: create の後に確認を取り消されると、認証器にだけ
 *      使えないパスキーが残る（選ぶと 401 になる = 本人を迷わせる）。Money の PasskeysPage と同じ順番
 *   2. 確認が「パスワード」なら、そのまま options → create → 登録（ダイアログの「確認」のクリックの続きとして create を呼べる）。
 *      確認が reauthToken（「パスキーで確認」・「Google で確認」から戻った）なら、**いったん止めて** もう一度「パスキーを作成」を押してもらう。
 *      WHY: パスキーでの確認は get のプロンプトを 1 回出した後。続けて create を呼ぶと、Safari はクリックから時間の空いた create を
 *      断ることがある（ユーザーの操作の中でしか呼べない）。Google から戻った場合はそもそもクリックが無い（Money の pk.reauthResumed と同じ）。
 *      トークンは 5 分・一回限り（メモリだけ）
 *   3. 登録で 400 / 401 の再認証を求められたら（証明の期限切れ等）、同じ credential のまま確認だけ取り直して送り直す。
 *      サーバーは再認証を challenge の消費より前に判定する（loginPasskeyRoutes.ts）= 作ったパスキーを捨てずに済む
 * 削除は WebAuthn を使わないので、「Google で確認」から戻ったら自動で続ける（LoginMethodsCard の unlink と同じ）。
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../api';
import { PasskeyIcon } from '../components/OAuthButtons';
import { useReauth, useReauthFailureText } from '../components/Reauth';
import { ConfirmDialog } from '../components/ui';
import { canRemoveLoginPasskey, isReauthError, type ReauthMethods, type ReauthProof } from '../devOAuth';
import { errorText, formatDate, useT } from '../i18n';
import { passkeyLimitFrom, passkeyLoginError } from '../loginOptions';
import {
  createLoginPasskey,
  loginPasskeyCreationOptions,
  registerLoginPasskey,
  removeLoginPasskey,
  type LoginPasskey,
  type LoginPasskeysResponse
} from '../loginPasskeyClient';
import { passkeysSupported } from '../passkey';
import { isMoneyNavHidden } from '../launchHidden';

/** 再認証の操作の名前（「Google で確認」から戻ったとき pending.action と比べる）。削除は対象の ID を含める（戻った後に対象を復元する） */
export const LOGIN_PASSKEY_ADD_ACTION = 'loginPasskey:add';
const removeAction = (id: string) => `loginPasskey:del:${id}`;
const removeIdFrom = (action: string) => (action.startsWith('loginPasskey:del:') ? action.slice('loginPasskey:del:'.length) || null : null);

type Notice = { tone: 'ok' | 'error'; text: string };

export function LoginPasskeysSection({
  passkeys,
  methods,
  linkedCount,
  available,
  onChanged
}: {
  /** 一覧（読み込み中・失敗は null = 行を出さない） */
  passkeys: LoginPasskey[] | null;
  /** 再認証の方法（パスワード・外部 ID・ログイン用パスキー） */
  methods: ReauthMethods;
  /** 付いている外部 ID の数（最後のログイン手段かの判断） */
  linkedCount: number;
  /** サーバーでパスキーのログインが使えるか（/v1/platform/info の passkeyLogin）。false でも既存の分は外せるように一覧は出す */
  available: boolean;
  onChanged: () => void;
}) {
  const { t } = useT();
  const reauth = useReauth();
  const failureText = useReauthFailureText();
  const supported = passkeysSupported();
  const [notice, setNotice] = useState<Notice | null>(null);
  // 「Google で確認」から戻って追加の途中 = 追加の欄を開いた状態で始める
  const [adding, setAdding] = useState(() => reauth.pending?.action === LOGIN_PASSKEY_ADD_ACTION);
  const [confirmRemove, setConfirmRemove] = useState<LoginPasskey | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const list = passkeys ?? [];
  const removable = canRemoveLoginPasskey({ hasPassword: methods.hasPassword, linkedCount, loginPasskeyCount: list.length });

  const remove = async (id: string) => {
    setBusy(`del:${id}`);
    setNotice(null);
    try {
      const r = await reauth.run((proof) => removeLoginPasskey(id, proof as { password?: string; reauthToken?: string }), {
        action: removeAction(id),
        returnTo: '/account',
        ask: methods
      });
      if (r) {
        onChanged();
        setNotice({ tone: 'ok', text: t('lpk.removed') });
      }
    } catch (err) {
      setNotice({ tone: 'error', text: errorText(err as ApiError) });
    } finally {
      setBusy(null);
    }
  };

  // 「Google で確認」から戻った削除は自動で続ける（1 回だけ・一覧が読めてから）
  const resumedOnce = useRef(false);
  useEffect(() => {
    const p = reauth.pending;
    if (!p || resumedOnce.current || !passkeys) return;
    const id = removeIdFrom(p.action);
    if (id && passkeys.some((k) => k.id === id)) {
      resumedOnce.current = true;
      void remove(id);
    }
  }, [reauth.pending, passkeys]);

  // 戻りで失敗した（別のアカウントを選んだ・キャンセル）
  useEffect(() => {
    const f = reauth.failure;
    if (!f || !f.action.startsWith('loginPasskey:')) return;
    setNotice({ tone: 'error', text: failureText(f) });
    reauth.consume();
  }, [reauth.failure]);

  if (!passkeys) return null;
  // サーバーで使えず、1 本も無い = 何もできない欄を見せない
  if (!available && list.length === 0) return null;

  return (
    <div className="lpk" id="login-passkeys">
      {reauth.dialog}
      <div className="lpk-head">
        <h3 className="lpk-title">{t('lpk.title')}</h3>
        <p className="muted small">{t('lpk.lead')}</p>
        {/* 出金用のパスキー（payouts）を見せない間は「出金用とは別物」の説明とリンクも出さない（2026-09-29 creator/launch-money） */}
        {!isMoneyNavHidden('passkeys') && (
          <p className="small lpk-separate">
            {t('lpk.separate')} <Link to="/money/passkeys">{t('lpk.moneyLink')} →</Link>
          </p>
        )}
      </div>
      {notice && (
        <div className={`alert ${notice.tone === 'ok' ? 'alert-ok' : 'alert-error'}`} role={notice.tone === 'ok' ? 'status' : 'alert'}>
          {notice.text}
        </div>
      )}
      {list.length === 0 ? (
        <p className="muted small">{t('lpk.empty')}</p>
      ) : (
        <ul className="login-methods">
          {list.map((k) => (
            <li key={k.id} className="login-method">
              <span className="login-method-name">
                <span className="login-method-icon">
                  <PasskeyIcon />
                </span>
                <strong>{k.label}</strong>
                <span className="muted small">
                  {t('login.addedOn', { date: formatDate(k.createdAt) })} · {k.lastUsedAt ? t('login.lastUsed', { date: formatDate(k.lastUsedAt) }) : t('lpk.neverUsed')}
                </span>
              </span>
              <span className="login-method-actions">
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setConfirmRemove(k)}
                  disabled={!removable || !!busy}
                  aria-describedby={removable ? undefined : 'lpk-last-note'}
                >
                  {busy === `del:${k.id}` ? t('common.working') : t('lpk.remove')}
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      {list.length > 0 && !removable && (
        <p id="lpk-last-note" className="small muted">
          {t('lpk.lastNote')}
        </p>
      )}

      {available &&
        (!supported ? (
          <p className="small muted">{t('lpk.unsupported')}</p>
        ) : adding ? (
          <AddLoginPasskey
            reauth={reauth}
            methods={methods}
            resumed={reauth.pending?.action === LOGIN_PASSKEY_ADD_ACTION}
            onCancel={() => setAdding(false)}
            onAdded={() => {
              setAdding(false);
              onChanged();
              setNotice({ tone: 'ok', text: t('lpk.added') });
            }}
          />
        ) : (
          <button type="button" className="btn btn-ghost btn-sm lpk-add" onClick={() => (setNotice(null), setAdding(true))} disabled={!!busy}>
            <PasskeyIcon />
            <span>{t('lpk.add')}</span>
          </button>
        ))}
      {list.length > 0 && <p className="small muted">{t('lpk.lost')}</p>}

      <ConfirmDialog
        open={!!confirmRemove}
        title={confirmRemove ? t('lpk.removeTitle', { label: confirmRemove.label }) : ''}
        confirmLabel={t('lpk.remove')}
        tone="danger"
        onClose={() => setConfirmRemove(null)}
        onConfirm={() => {
          const k = confirmRemove;
          setConfirmRemove(null);
          if (k) void remove(k.id);
        }}
      >
        <p>{t('lpk.removeBody')}</p>
        <p className="small">{t('login.signOutOthers')}</p>
      </ConfirmDialog>
    </div>
  );
}

type Reauth = ReturnType<typeof useReauth>;

/** 追加の欄（名前 + 「パスキーを作成」）。順番の WHY はファイルの冒頭 */
function AddLoginPasskey({
  reauth,
  methods,
  resumed,
  onCancel,
  onAdded
}: {
  reauth: Reauth;
  methods: ReauthMethods;
  /** 「Google で確認」から戻ってきた（証明がメモリにある）= もう一度押せば続く */
  resumed: boolean;
  onCancel: () => void;
  onAdded: (p: LoginPasskey) => void;
}) {
  const { t } = useT();
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // 「パスキーで確認」で得た証明（5 分・一回限り）。もう一度押したときに使う（メモリだけ・URL やストレージに置かない）
  const [held, setHeld] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      // 1. 本人の確認（create より前）。持っている証明 → 戻ってきた証明 → ダイアログ の順
      const resumedToken = held ?? reauth.takePending(LOGIN_PASSKEY_ADD_ACTION);
      let proof: ReauthProof | null = resumedToken ? { reauthToken: resumedToken } : await reauth.requestReauth(methods, { action: LOGIN_PASSKEY_ADD_ACTION, returnTo: '/account' });
      if (!proof) return;
      if (!resumedToken && 'reauthToken' in proof) {
        // ダイアログの「パスキーで確認」で得た = get のプロンプトの直後。create はもう一度のクリックで（冒頭 2）
        setHeld(proof.reauthToken);
        return;
      }
      setHeld(null);
      // 2. options → 認証器で作る（excludeCredentials = この端末に既にあれば InvalidStateError）
      const opts = await loginPasskeyCreationOptions();
      const credential = await createLoginPasskey(opts.publicKey);
      // 3. 登録。再認証を求められたら同じ credential のまま確認だけ取り直す（上限はサーバーの回数の制限が正・ここは無限に回らないため）
      for (let i = 0; i < 5 && proof; i++) {
        try {
          const r = await registerLoginPasskey({ credential, label: label.trim() || undefined, ...proof });
          onAdded(r.passkey);
          return;
        } catch (e2) {
          if (!isReauthError(e2)) throw e2;
          proof = await reauth.requestReauth(e2 as ApiError, { action: LOGIN_PASSKEY_ADD_ACTION, returnTo: '/account' });
        }
      }
    } catch (e2) {
      const name = (e2 as { name?: unknown } | null)?.name;
      // 上限は数を写さず、断られたときのサーバーの details.limit で言う（loginOptions.ts passkeyLimitFrom の WHY）
      const limit = passkeyLimitFrom(e2);
      if (name === 'InvalidStateError') setErr(t('lpk.alreadyOnDevice'));
      else if (limit !== null) setErr(t('lpk.max', { max: limit }));
      else if (passkeyLoginError(e2) === 'cancel') setErr(t('lpk.cancelled'));
      else if (e2 instanceof ApiError && e2.code === 'passkey_login_unavailable') setErr(t('lpk.unavailable'));
      else setErr(e2 instanceof ApiError ? errorText(e2) : t('reauth.passkeyFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="lpk-add-form" onSubmit={(e) => void submit(e)}>
      <h4 className="lpk-subtitle">{t('lpk.addTitle')}</h4>
      {(resumed || held) && (
        <p className="alert alert-ok" role="status">
          {t('lpk.reauthResumed')}
        </p>
      )}
      <label className="field" htmlFor="lpk-label">
        <span>
          {t('lpk.label')}
          <small>{t('common.optional')}</small>
        </span>
        {/* autocomplete off: 名前の欄をユーザー名と取り違えてパスワード管理が保存しないように（Money の PasskeysPage と同じ） */}
        <input id="lpk-label" name="login-passkey-label" autoComplete="off" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={60} placeholder={t('lpk.labelPlaceholder')} />
      </label>
      {!resumed && !held && <p className="small muted">{t('lpk.addHint')}</p>}
      {err && (
        <p className="alert alert-error" role="alert">
          {err}
        </p>
      )}
      <div className="form-actions">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>
          {t('common.cancel')}
        </button>
        <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>
          {busy ? t('common.working') : t('lpk.create')}
        </button>
      </div>
    </form>
  );
}

/** カードが読む一覧の型（再輸出・AccountLoginMethods.tsx から使う） */
export type { LoginPasskeysResponse };
